import React, { ChangeEvent, FormEventHandler, useState } from 'react'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormCheckboxField,
  FormDateField,
  FormField,
  FormMultiSelectField,
  FormSelectField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { GridColumn as Column, Grid, GridCellProps } from '@progress/kendo-react-grid'
import { Language } from 'api/Clients/Types/Language'
import { TickmillCompanyDto } from 'api/Identity/Types/Company'
import { DateFormatType } from 'api/Identity/Types/DateFormatType'
import { DepartmentDto } from 'api/Identity/Types/DepartmentDto'
import { RoleDto } from 'api/Identity/Types/Role'
import { TeamDto } from 'api/Identity/Types/Team'
import { ClientAutoAssignmentDto, UserResponse } from 'api/Identity/Types/UserResponse'
import MoreButton from 'components/MoreButton/MoreButton'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import {
  FormSubmitValues,
  LowerCasePattern,
  NumberPattern,
  SymbolPattern,
  UpperCasePattern,
} from 'utils/formValidation'
import stringComparator from 'utils/stringComparator'
import { zonedTimeToUtc } from 'utils/time-zone/helpers'

export interface AddEditUserTeamValues {
  team?: { id: string; name: string }
  eligibleToClientAutoAssignment: boolean
  visibility: boolean
  language?: { id: string; name: string }
  isEdit?: boolean
}

export interface AddEditUserTeamFormValues {
  teamIds: string[]
  languageId: string
  eligibleToClientAutoAssignment: boolean
  visibility: boolean
}

export interface AddEditUserFormValues {
  id?: string
  firstName: string
  lastName: string
  email: string
  password?: string
  kindId: number
  statusId: number | null
  departmentsIds: number[]
  dateFormatTypeId: number
  roleIds: string[]
  teams: AddEditUserTeamValues[]
  tickmillCompanyId?: number
  isAutoAssignmentPaused: boolean
  autoAssignmentPausedStart: string | null
  autoAssignmentPausedUntil: string | null
  isLocal: boolean
}

const AddEditUserFormUI: React.FC<FormikProps<AddEditUserFormValues> & OuterProps> = (props) => {
  const { handleSubmit, setSubmitting, isSubmitting, errors, setFieldValue, values } = props
  const isNewUser = !props.values.id

  const [formValues, setFormValues] = useState<AddEditUserTeamFormValues>({
    teamIds: [],
    languageId: '',
    eligibleToClientAutoAssignment: false,
    visibility: false,
  })

  const handleSubmitForm = () => {
    setSubmitting(true)
    handleSubmit()
  }

  const handleAddTeam: FormEventHandler = (event) => {
    event.preventDefault()

    const language = props.languages.find((x) => x.id === formValues.languageId)
    const teams = formValues.teamIds
      .map((teamId) => props.teams.find((x) => x.id === teamId))
      .filter((x): x is TeamDto => !!x)
      .map((x) => ({
        team: { id: x.id, name: x.name },
        language,
        eligibleToClientAutoAssignment: formValues.eligibleToClientAutoAssignment,
        visibility: formValues.visibility,
      }))

    setFieldValue('teams', [...values.teams, ...teams])
    setFormValues({
      teamIds: [],
      languageId: '',
      eligibleToClientAutoAssignment: false,
      visibility: false,
    })
  }

  const handleEditTeam = (team: AddEditUserTeamValues) => {
    setFieldValue(
      'teams',
      values.teams.map((x) =>
        x.team?.id === team.team?.id
          ? {
              ...x,
              isEdit: !team.isEdit,
            }
          : x
      )
    )
  }

  const handleAutoAssignment = (team: AddEditUserTeamValues) => {
    setFieldValue(
      'teams',
      values.teams.map((x) =>
        x.team?.id === team.team?.id
          ? {
              ...x,
              eligibleToClientAutoAssignment: !team.eligibleToClientAutoAssignment,
            }
          : x
      )
    )
  }

  const handleVisibility = (team: AddEditUserTeamValues) => {
    setFieldValue(
      'teams',
      values.teams.map((x) =>
        x.team?.id === team.team?.id
          ? {
              ...x,
              visibility: !team.visibility,
            }
          : x
      )
    )
  }

  const handleCommunicationLanguage = (
    event: ChangeEvent<HTMLSelectElement>,
    team: AddEditUserTeamValues
  ) => {
    const language = props.languages.find((x) => x.id === event.target.value)
    setFieldValue(
      'teams',
      values.teams.map((x) =>
        x.team?.id === team.team?.id
          ? {
              ...x,
              language,
            }
          : x
      )
    )
  }

  const isValid = () => {
    return formValues.teamIds && formValues.teamIds.length >= 1
  }

  return (
    <>
      <Form>
        <DataGrid
          rightAddon={
            <ButtonGroup>
              <Button disabled={isSubmitting} onClick={handleSubmitForm} type='button'>
                Submit
              </Button>
            </ButtonGroup>
          }
        >
          <DataColumn>
            <DataCard title='General' columns={2}>
              <FormField
                dataTest='user-firstName-field'
                type='text'
                name='firstName'
                label='Firstname'
                error={errors.firstName}
              />
              <FormField
                dataTest='user-lastName-field'
                type='text'
                name='lastName'
                label='Lastname'
                error={errors.lastName}
              />
              <FormField
                dataTest='user-email-field'
                type='text'
                name='email'
                label='E-mail'
                error={errors.email}
              />
              {isNewUser && (
                <FormField
                  dataTest='user-password-field'
                  type='password'
                  name='password'
                  label='Password'
                  error={errors.password}
                />
              )}
              <FormMultiSelectField
                name='roleIds'
                label='Roles'
                options={props.roles}
                value={values.roleIds}
                showSearch
                allowClear
                dataTest='user-roles-field'
                error={errors.roleIds as string}
                onChange={(value) => {
                  const nextValue = Array.isArray(value)
                    ? value.map(String)
                    : value == null
                      ? []
                      : [String(value)]
                  setFieldValue('roleIds', nextValue)
                }}
              />
              <FormSelectField
                name='dateFormatTypeId'
                label='Date format'
                options={props.dateFormat}
                error={errors.dateFormatTypeId}
                onChange={(value) => setFieldValue('dateFormatTypeId', Number(value))}
              />
              <FormMultiSelectField
                name='departmentsIds'
                label='Departments'
                options={props.departments}
                value={values.departmentsIds}
                showSearch
                allowClear
                dataTest='user-departmentsIds-field'
                error={errors.departmentsIds as string}
                onChange={(value) => {
                  const nextValue = Array.isArray(value)
                    ? value.map(Number)
                    : value == null
                      ? []
                      : [Number(value)]
                  setFieldValue('departmentsIds', nextValue)
                }}
              />
              <FormCheckboxField name='isLocal' label='Local User (not AAD)' />
              {!isNewUser && (
                <FormSelectField
                  name='statusId'
                  label='Status'
                  options={[
                    { id: 1, name: 'Confirmed' },
                    { id: 2, name: 'Locked' },
                  ]}
                  onChange={(value) => setFieldValue('statusId', value ? Number(value) : null)}
                />
              )}
              <FormCheckboxField
                name='isAutoAssignmentPaused'
                label='Paused Auto Assignment'
                error={errors.isAutoAssignmentPaused}
                onChange={(value) => {
                  if (!value) {
                    setFieldValue('autoAssignmentPausedStart', null)
                    setFieldValue('autoAssignmentPausedUntil', null)
                  }
                  setFieldValue('isAutoAssignmentPaused', value)
                }}
              />
              <FormDateField
                name='autoAssignmentPausedStart'
                label='Pause Start'
                dateFormatType='dd/MM/yyyy'
                isClearable
                disabled={!values.isAutoAssignmentPaused}
                onChange={(value) => {
                  setFieldValue('autoAssignmentPausedStart', value?.toISOString() || null)
                }}
              />
              <FormDateField
                name='autoAssignmentPausedUntil'
                label='Pause Until'
                dateFormatType='dd/MM/yyyy'
                isClearable
                disabled={!values.isAutoAssignmentPaused}
                onChange={(value) => {
                  setFieldValue('autoAssignmentPausedUntil', value?.toISOString() || null)
                }}
              />
            </DataCard>
          </DataColumn>

          <DataColumn>
            <DataCard title='Current Teams'>
              <Grid data={props.values.teams} total={(props.values.teams || []).length}>
                <Column field='team.name' title='Team' />
                <Column
                  title='Auto-Assignment'
                  cell={(gridCellProps) => (
                    <td align='center' style={{ textAlign: 'center' }}>
                      <input
                        type='checkbox'
                        checked={gridCellProps.dataItem.eligibleToClientAutoAssignment}
                        onChange={() => handleAutoAssignment(gridCellProps.dataItem)}
                        disabled={!gridCellProps.dataItem.isEdit}
                      />
                    </td>
                  )}
                />
                <Column
                  title='Visibility'
                  cell={(gridCellProps) => (
                    <td align='center' style={{ textAlign: 'center' }}>
                      <input
                        type='checkbox'
                        checked={gridCellProps.dataItem.visibility}
                        onChange={() => handleVisibility(gridCellProps.dataItem)}
                        disabled={!gridCellProps.dataItem.isEdit}
                      />
                    </td>
                  )}
                />
                <Column
                  field='language.name'
                  title='Communication Language'
                  cell={(gridCellProps) => {
                    if (gridCellProps.dataItem.isEdit) {
                      return (
                        <td align='center' style={{ textAlign: 'center' }}>
                          <select
                            value={gridCellProps.dataItem.language?.id}
                            onChange={(event) =>
                              handleCommunicationLanguage(event, gridCellProps.dataItem)
                            }
                          >
                            <option value={''}>Not set</option>
                            {props.languages.map((language) => (
                              <option key={language.id} value={language.id}>
                                {language.name}
                              </option>
                            ))}
                          </select>
                        </td>
                      )
                    }

                    return (
                      <td align='center' style={{ textAlign: 'center' }}>
                        {gridCellProps.dataItem.language?.name || ''}
                      </td>
                    )
                  }}
                />
                <Column
                  width={50}
                  title=''
                  filterCell={() => null}
                  cell={(gridCellProps: GridCellProps) => (
                    <td>
                      <MoreButton
                        items={[
                          {
                            name: 'Edit',
                            'data-test': 'edit',
                            onClick: () => handleEditTeam(gridCellProps.dataItem),
                          },
                          {
                            name: 'Remove',
                            'data-test': 'remove',
                            onClick: () => {
                              const newTeams = props.values.teams.filter(
                                (team) => team.team?.id !== gridCellProps.dataItem?.team.id
                              )
                              setFieldValue('teams', newTeams)
                            },
                          },
                        ]}
                        data-test='more-button'
                      />
                    </td>
                  )}
                />
              </Grid>
            </DataCard>
            <DataCard title='Teams' columns={1}>
              <FormMultiSelectField
                label='Team'
                options={props.teams
                  .filter(
                    (team) => !props.values.teams.map((value) => value.team?.id).includes(team.id)
                  )
                  .map((team) => ({ id: team.id, name: team.name }))}
                value={formValues.teamIds}
                showSearch
                onChange={(value) => {
                  const nextValue = Array.isArray(value)
                    ? value.map(String)
                    : value == null
                      ? []
                      : [String(value)]

                  setFormValues({
                    ...formValues,
                    teamIds: nextValue,
                  })
                }}
              />
              <FormCheckboxField
                label='Auto-Assignment'
                value={formValues.eligibleToClientAutoAssignment}
                onChange={(value) => {
                  setFormValues({
                    ...formValues,
                    eligibleToClientAutoAssignment: value,
                  })
                }}
              />
              <FormCheckboxField
                label='Visibility'
                value={formValues.visibility}
                onChange={(value) => {
                  setFormValues({
                    ...formValues,
                    visibility: value,
                  })
                }}
              />
              <FormSelectField
                label='Communication Language'
                options={props.languages}
                value={formValues.languageId}
                onChange={(value) => {
                  setFormValues({ ...formValues, languageId: value ? String(value) : '' })
                }}
              />
              <ButtonGroup className='justify-end'>
                <Button disabled={!isValid()} onClick={handleAddTeam} type='button'>
                  Add
                </Button>
              </ButtonGroup>
            </DataCard>
          </DataColumn>
        </DataGrid>
      </Form>
    </>
  )
}

interface OuterProps {
  user?: UserResponse
  values?: AddEditUserFormValues
  roles: RoleDto[]
  teams: TeamDto[]
  languages: Language[]
  departments: DepartmentDto[]
  dateFormat: DateFormatType[]
  tickmillCompanies: TickmillCompanyDto[]

  onSubmit(values: FormSubmitValues<AddEditUserFormValues>): Promise<void>
}

function dataToSubmit(values: AddEditUserFormValues): FormSubmitValues<AddEditUserFormValues> {
  return {
    ...values,
    autoAssignmentPausedStart: values.autoAssignmentPausedStart
      ? zonedTimeToUtc(new Date(values.autoAssignmentPausedStart)).toISOString()
      : null,
    autoAssignmentPausedUntil: values.autoAssignmentPausedUntil
      ? zonedTimeToUtc(new Date(values.autoAssignmentPausedUntil)).toISOString()
      : null,
  }
}

function mapUserResponseToEditUserFormValues(
  data: Partial<UserResponse> | undefined,
  dateFormat: DateFormatType[],
  roles: RoleDto[],
  values: AddEditUserFormValues | undefined
) {
  if (values) return values
  const currentUser: Partial<UserResponse> = data || {}

  return {
    id: currentUser?.id,
    email: currentUser?.email || '',
    firstName: currentUser?.firstName || '',
    lastName: currentUser?.lastName || '',
    roleIds: toRoleTeamRecord(roles, currentUser.roles || []),
    dateFormatTypeId: currentUser?.dateFormatType?.id || dateFormat[0].id,
    password: '',
    teams: toSelectedTeamDTO(currentUser.userTeams).sort((a, b) =>
      stringComparator(a.team?.name, b.team?.name)
    ),
    departmentsIds: (currentUser?.departments || []).map((x) => x.id) || null,
    statusId: currentUser?.status ? currentUser.status.id : null,
    kindId: 1,
    userStatusId: currentUser.status?.id,
    isLocal: currentUser.isLocal || false,
    isAutoAssignmentPaused: currentUser.isAutoAssignmentPaused || false,
    autoAssignmentPausedStart: currentUser?.autoAssignmentPausedStart || null,
    autoAssignmentPausedUntil: currentUser?.autoAssignmentPausedUntil || null,
  }
}
export const AddEditUserForm = withFormik<OuterProps, AddEditUserFormValues>({
  mapPropsToValues: ({ user, dateFormat, roles, values }) =>
    mapUserResponseToEditUserFormValues(user, dateFormat, roles, values),
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<AddEditUserFormValues> = {}
    if (!values.dateFormatTypeId) {
      errors.dateFormatTypeId = 'required'
    }
    if (!values.roleIds.length) {
      errors.roleIds = 'required'
    }
    if (!values.firstName) {
      errors.firstName = 'required'
    }
    if (!values.lastName) {
      errors.lastName = 'required'
    }
    if (!values.email) {
      errors.email = 'required'
    }
    if (!values.id) {
      if (!values.password) {
        errors.password = 'required'
      } else if (values.password.length < 12) {
        errors.password = 'The password should be at least 12 characters.'
      } else if (values.password.length > 128) {
        errors.password = 'The password maximum length is 128 characters.'
      } else if (!UpperCasePattern.test(values.password)) {
        errors.password = 'The password needs contain at least one upper character.'
      } else if (!LowerCasePattern.test(values.password)) {
        errors.password = 'The password needs contain at least one lower character.'
      } else if (!NumberPattern.test(values.password)) {
        errors.password = 'The password needs contain at least one digit'
      } else if (!SymbolPattern.test(values.password)) {
        errors.password = 'The password must contain at least 1 symbol (*@!#%&()^~{}+%$_-)'
      }
    }
    return errors
  },
  enableReinitialize: true,
})(AddEditUserFormUI)

const toSelectedTeamDTO = (teams: ClientAutoAssignmentDto[] = []): AddEditUserTeamValues[] => {
  return teams.map((x) => ({
    team: { id: x.team.id, name: x.team.name },
    language: x.language ? { id: x?.language.id, name: x?.language.name } : undefined,
    eligibleToClientAutoAssignment: x.eligible,
    visibility: x.visibility,
  }))
}

const toRoleTeamRecord = (data: (RoleDto | TeamDto)[], names: string[]): string[] => {
  return data.reduce<string[]>((acc, role) => {
    if (names.includes(role.name)) {
      return acc.concat(role.id)
    }
    return acc
  }, [])
}
